import { Alert, Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PlayerPicker } from '../components/PlayerPicker';
import { useSignInWithPin } from '../hooks/useAuthActions';
import { useRosterForSignIn } from '../hooks/useRoster';
import { getErrorMessage } from '../services/api-client';
import { colors } from '../themes/theme';

/**
 * Sign-in for someone who already has an account but no session -- a new phone,
 * or cleared browser data. Creating an account needs the shared link; this
 * screen only signs existing accounts in.
 */
export function SignIn() {
    const navigate = useNavigate();
    const roster = useRosterForSignIn();
    const signIn = useSignInWithPin();

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                px: 3,
                // Clear of the notch at the top and the home indicator below.
                pt: 'calc(32px + env(safe-area-inset-top))',
                pb: 'calc(32px + env(safe-area-inset-bottom))',
                maxWidth: 460,
                mx: 'auto',
            }}
        >
            <Stack spacing={3}>
                <Box>
                    <Typography variant="overline" sx={{ color: colors.safety }}>
                        Settle it
                    </Typography>
                    <Typography variant="h1" sx={{ fontSize: 'clamp(2.6rem, 14vw, 4.5rem)' }}>
                        Pony Up
                    </Typography>
                </Box>

                {roster.isPending && <Typography color="text.secondary">Loading…</Typography>}

                {roster.isError && (
                    <Alert severity="error">{getErrorMessage(roster.error)}</Alert>
                )}

                {roster.data && roster.data.length === 0 && (
                    <Alert severity="info">
                        Nobody has signed up yet. Open the invite link you were sent to get
                        started.
                    </Alert>
                )}

                {roster.data && roster.data.length > 0 && (
                    <PlayerPicker
                        players={roster.data}
                        pending={signIn.isPending}
                        error={signIn.isError ? getErrorMessage(signIn.error) : null}
                        onSubmit={(userId, pin) =>
                            signIn.mutate(
                                { userId, pin },
                                { onSuccess: () => navigate('/', { replace: true }) },
                            )
                        }
                    />
                )}

                <Typography variant="body2" sx={{ color: colors.muted }}>
                    New here? You&rsquo;ll need the link from the group chat.
                </Typography>
            </Stack>
        </Box>
    );
}
